import React, { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { createUseStyles } from 'react-jss'
import { useContainerStyles } from '../../../theme/layout'
import { colors } from '../../../theme/palette'
import { Typography } from '../Typography/Typography'
import { getProfileSupportInfo } from '../../../api/profile/profile'
import breakpoints from '../../../theme/breakpoints'
import { formatPhoneNumber } from '../../../utils/format'
import { FirstLoginInfo } from '../../modules/FirstLoginModal/FirstLoginModal'
import { L_HTML } from '../../providers/Lokalise'

const useStyles = createUseStyles(() => ({
    container: {
        backgroundColor: colors.green500,
        width: '100%',
        minHeight: '20rem',
        zIndex: 1,
        padding: '20px',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                padding: '40px 0 0',
            },
        },
    },
    inner: {
        display: 'grid',
        gridTemplateColumns: '1fr',
        gap: '40px',
        height: '100%',
        [`@media (min-width: ${breakpoints.Desktop}px)`]: {
            '&': {
                gridTemplateColumns: '1fr 1fr 1fr',
                gap: '20%',
            },
        },
    },
    contactLink: {
        textDecoration: 'underline',
        color: 'inherit',
        cursor: 'pointer',
        '&:hover, &:focus, &:active': {
            opacity: '0.8',
        },
    },
    column: {},
    videoLink: {
        backgroundColor: 'transparent',
        color: 'inherit',
        border: 'none',
        fontSize: '1rem',
        textDecoration: 'underline',
        padding: '0',
        margin: '0',
        textAlign: 'start',
        cursor: 'pointer',
        '&:hover': {
            opacity: '0.8',
        },
    },
}))

const Footer: React.FC = () => {
    const classes = useStyles()
    const layout = useContainerStyles()
    const [info, setInfo] = useState<any | undefined>(undefined)
    const [showVideo, setShowVideo] = useState<boolean>(false)
    const [col1, setCol1] = useState<any | undefined>(undefined)

    const location = useLocation()
    const shouldShowFirstLoginInfo = !location.pathname.includes(
        '/mine-lonindplaceringer'
    )

    useEffect(() => {
        const getInfo = async (): Promise<void> => {
            const response = await getProfileSupportInfo()

            const found = response.data.KontaktoplysningerList.find(
                (item: any) => item.KontaktoplysningerType === 'Afdeling'
            )

            const firstColumn = response.data.KontaktoplysningerList.find(
                (item: any) => item.KontaktoplysningerType === 'TRHotline'
            )
            if (found) {
                setInfo(found)
            }
            if (firstColumn) {
                setCol1(firstColumn)
            }
        }

        getInfo()
    }, [])

    const closeVideo = (): void => {
        setShowVideo(false)
    }

    return (
        <div className={classes.container} id="footer">
            <div className={`${layout.container} ${classes.inner}`}>
                <div className={classes.column}>
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                        weight="bold"
                    >
                        Tekniske udfordringer
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        Kontakt hotline for tillidsvalgte:
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        Tlf:{' '}
                        {col1 && col1.Telefon
                            ? formatPhoneNumber(col1.Telefon)
                            : ''}
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        <a
                            href={`mailto:${col1 && col1.Email}`}
                            className={classes.contactLink}
                        >
                            {(col1 && col1.Email) || ''}
                        </a>
                    </Typography>
                </div>
                <div className={classes.column}>
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                        weight="bold"
                    >
                        Fejl i medlemsdata
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        Kontakt din FOA-afdeling:
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        Tlf:{' '}
                        {info && info.Telefon
                            ? formatPhoneNumber(info.Telefon)
                            : ''}
                    </Typography>
                    <br />
                    {info && info.Email ? (
                        <Typography
                            tag="p"
                            size="XS"
                            spacing=".2rem"
                            color={colors.white}
                        >
                            <a
                                href={`mailto:${info.Email}`}
                                className={classes.contactLink}
                            >
                                {(info && info.Email) || ''}
                            </a>
                        </Typography>
                    ) : null}
                </div>
                <div className={classes.column}>
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                        weight="bold"
                    >
                        Andet
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        <button
                            type="button"
                            onClick={() => setShowVideo(true)}
                            className={classes.videoLink}
                        >
                            {L_HTML('first_login_footer_link_text')}
                        </button>
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        <a
                            href="https://www.foa.dk/~/media/Faelles/PDF/Tillidsvalgte - SKAL MED OVER/minemedlemmer/pdf/quickguide"
                            target="_blank"
                            className={classes.contactLink}
                            rel="noreferrer"
                        >
                            Quick guide
                        </a>
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        <a
                            href="https://www.foa.dk/~/media/Faelles/PDF/Tillidsvalgte - SKAL MED OVER/minemedlemmer/pdf/brugervejledning"
                            target="_blank"
                            className={classes.contactLink}
                            rel="noreferrer"
                        >
                            Brugervejledning
                        </a>
                    </Typography>
                    <br />
                    <Typography
                        tag="p"
                        size="XS"
                        spacing=".2rem"
                        color={colors.white}
                    >
                        <a
                            href="mailto:tillidsvalgt@foa.dk"
                            className={classes.contactLink}
                        >
                            Ideer/forslag?
                        </a>
                    </Typography>
                </div>
            </div>
            {shouldShowFirstLoginInfo && (
                <FirstLoginInfo showVideo={showVideo} setClose={closeVideo} />
            )}
        </div>
    )
}

export { Footer }
